import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { UnmappedDevicePunchService } from '../../services/unmapped-device-punch.service';
import { DeviceApiKeyService } from '../../services/device-api-key.service';
import {
    UnmappedPunchGroup,
    UnmappedDevicePunch,
    UnmappedPunchReplayResult
} from '../../interfaces/unmappedDevicePunch.interface';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Pagination,
    TextField
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { EmployeeAutocomplete, EmployeeOption, employeeLabelWithFullName } from '../common/EmployeeAutocomplete';
import { ConfirmDialog } from '../common/ConfirmDialog';
import ScrollableTable from '../common/ScrollableTable';

interface DeviceOption {
    id: string;
    deviceSn: string | null;
}

const REASON_LABEL: Record<string, string> = {
    UNKNOWN_DEVICE_USER: 'Unknown device user',
    UNMAPPED_DEVICE_USER: 'Unmapped device user'
};

interface UnmappedPunchListProps {
    /** Called after every successful assign or discard so the parent can refetch the count badge. */
    onChanged: () => void;
}

const UnmappedPunchList: React.FC<UnmappedPunchListProps> = ({ onChanged }) => {
    const toast = useToast();

    const [groups, setGroups] = useState<UnmappedPunchGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

    const [deviceOptions, setDeviceOptions] = useState<DeviceOption[]>([]);
    const [deviceSerialFilter, setDeviceSerialFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Assign (map to employee) dialog
    const [assignTarget, setAssignTarget] = useState<UnmappedPunchGroup | null>(null);
    const [assignEmployee, setAssignEmployee] = useState<EmployeeOption | null>(null);
    const [assigning, setAssigning] = useState(false);

    // View punches dialog
    const [viewTarget, setViewTarget] = useState<UnmappedPunchGroup | null>(null);
    const [viewPunches, setViewPunches] = useState<UnmappedDevicePunch[]>([]);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewPageInfo, setViewPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

    // Discard confirmation
    const [discardTarget, setDiscardTarget] = useState<UnmappedPunchGroup | null>(null);
    const [discarding, setDiscarding] = useState(false);

    useEffect(() => {
        DeviceApiKeyService.getByInstitution()
            .then((keys: DeviceOption[]) => setDeviceOptions(keys || []))
            .catch(() => toast.showError('Failed to load devices'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        fetchGroups(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceSerialFilter, debouncedSearch]);

    const fetchGroups = async (page: number = 0) => {
        try {
            setLoading(true);
            const response = await UnmappedDevicePunchService.getGroups(
                page,
                10,
                deviceSerialFilter || undefined,
                debouncedSearch || undefined
            );
            setGroups(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load unmapped punches');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchGroups(page - 1);
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return format(parseISO(dateStr), 'dd-MM-yyyy HH:mm');
    };

    // --- Assign (map to employee) ---

    const openAssignDialog = (group: UnmappedPunchGroup) => {
        setAssignTarget(group);
        setAssignEmployee(null);
    };

    const closeAssignDialog = () => {
        setAssignTarget(null);
        setAssignEmployee(null);
    };

    const handleAssign = async () => {
        if (!assignTarget || !assignEmployee) return;
        try {
            setAssigning(true);
            const result: UnmappedPunchReplayResult = await UnmappedDevicePunchService.assign(
                assignTarget.deviceSerial,
                assignTarget.deviceUserNumber,
                assignEmployee.id
            );
            let message =
                `Mapped and replayed ${result.punchesReplayed} punch(es) — ` +
                `${result.attendanceCreated} attendance rows created, ${result.attendanceUpdated} updated`;
            if (result.punchesStillHeld > 0) {
                message += `. Warning: ${result.punchesStillHeld} punch(es) are still held and were not replayed.`;
            }
            toast.showSuccess(message);
            closeAssignDialog();
            await fetchGroups(pageInfo.currentPage);
            onChanged();
        } catch (err: any) {
            toast.showApiError(err, 'Failed to map device user to employee');
        } finally {
            setAssigning(false);
        }
    };

    // --- View punches ---

    const openViewDialog = (group: UnmappedPunchGroup) => {
        setViewTarget(group);
        fetchViewPunches(group, 0);
    };

    const closeViewDialog = () => {
        setViewTarget(null);
        setViewPunches([]);
    };

    const fetchViewPunches = async (group: UnmappedPunchGroup, page: number) => {
        try {
            setViewLoading(true);
            const response = await UnmappedDevicePunchService.getPunches(
                group.deviceSerial,
                group.deviceUserNumber,
                page,
                10
            );
            setViewPunches(response.content || []);
            setViewPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load punches');
        } finally {
            setViewLoading(false);
        }
    };

    const handleViewPageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        if (viewTarget) fetchViewPunches(viewTarget, page - 1);
    };

    // --- Discard ---

    const handleDiscard = async () => {
        if (!discardTarget) return;
        try {
            setDiscarding(true);
            await UnmappedDevicePunchService.discard(discardTarget.deviceSerial, discardTarget.deviceUserNumber);
            toast.showSuccess('Held punches discarded');
            setDiscardTarget(null);
            await fetchGroups(pageInfo.currentPage);
            onChanged();
        } catch (err: any) {
            toast.showApiError(err, 'Failed to discard held punches');
        } finally {
            setDiscarding(false);
        }
    };

    if (loading && groups.length === 0) return <div>Loading...</div>;

    return (
        <div data-help-section="deviceUser.punches.section">
            <Box data-help-section="deviceUser.punches.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                    data-help="deviceUser.punches.filterDevice"
                    select
                    label="Device"
                    size="small"
                    value={deviceSerialFilter}
                    onChange={(e) => setDeviceSerialFilter(e.target.value)}
                    sx={{ flex: '1 1 260px', maxWidth: 320 }}
                >
                    <MenuItem value="">All devices</MenuItem>
                    {deviceOptions.filter(d => d.deviceSn).map(d => (
                        <MenuItem key={d.id} value={d.deviceSn as string}>{d.deviceSn}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    data-help="deviceUser.punches.search"
                    label="Search by enrolment no. or device name"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: '1 1 260px', maxWidth: 320 }}
                />
            </Box>
            <div style={{ marginBottom: '20px' }}>
                Total: {pageInfo.totalElements}
            </div>
            <ScrollableTable>
                <table data-help-section="deviceUser.punches.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                    <thead>
                        <tr>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.device">Device</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.enrolment">Enrolment no.</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.deviceName">Device name</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.reason">Reason</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.count">Punches</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.first">First seen</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.last">Last seen</th>
                            <th style={tableHeaderStyle} data-help="deviceUser.punches.col.actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map(g => (
                            <tr key={`${g.deviceSerial}-${g.deviceUserNumber}`}>
                                <td style={tableCellStyle}>{g.deviceSerial}</td>
                                <td style={tableCellStyle}>{g.deviceUserNumber}</td>
                                <td style={tableCellStyle}>{g.deviceUsername || '-'}</td>
                                <td style={tableCellStyle}>
                                    <Chip label={REASON_LABEL[g.reason] || g.reason} size="small" variant="outlined" />
                                </td>
                                <td style={tableCellStyle}>{g.count}</td>
                                <td style={tableCellStyle}>{formatDate(g.firstPunchAt)}</td>
                                <td style={tableCellStyle}>{formatDate(g.lastPunchAt)}</td>
                                <td style={tableCellStyle}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button data-help="deviceUser.punches.action.map" size="small" variant="outlined" onClick={() => openAssignDialog(g)}>
                                            Map to employee
                                        </Button>
                                        <Button data-help="deviceUser.punches.action.view" size="small" variant="outlined" onClick={() => openViewDialog(g)}>
                                            View punches
                                        </Button>
                                        <Button
                                            data-help="deviceUser.punches.action.discard"
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => setDiscardTarget(g)}
                                        >
                                            Discard
                                        </Button>
                                    </Box>
                                </td>
                            </tr>
                        ))}
                        {groups.length === 0 && (
                            <tr>
                                <td style={tableCellStyle} colSpan={8}>
                                    No held punches found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </ScrollableTable>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Map to employee dialog */}
            <Dialog open={!!assignTarget} onClose={assigning ? undefined : closeAssignDialog} fullWidth PaperProps={{ 'data-help-page': 'deviceUser.assign.page' } as any} maxWidth="sm">
                <DialogTitle>Map to employee</DialogTitle>
                <DialogContent>
                    {assignTarget && (
                        <Box sx={{ mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                            Device: {assignTarget.deviceSerial} | Enrolment no.: {assignTarget.deviceUserNumber}
                            {assignTarget.deviceUsername && ` | Name: ${assignTarget.deviceUsername}`}
                            {' | '}{assignTarget.count} held punch(es)
                        </Box>
                    )}
                    <EmployeeAutocomplete
                        data-help="deviceUser.assign.field.employee" label="Select employee"
                        placeholder="Search employees..."
                        fullWidth
                        getOptionLabel={employeeLabelWithFullName}
                        value={assignEmployee}
                        onChange={setAssignEmployee}
                        onError={() => toast.showError('Failed to search employees')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="deviceUser.assign.cancel" onClick={closeAssignDialog} disabled={assigning}>Cancel</Button>
                    <Button
                        data-help="deviceUser.assign.save" onClick={handleAssign}
                        variant="contained"
                        color="primary"
                        disabled={!assignEmployee || assigning}
                        {...helpWhy(assigning ? { kind: 'status', message: 'The punches are being replayed.' } : !assignEmployee ? { kind: 'status', message: 'Pick the employee first.' } : null)}
                        startIcon={assigning ? <CircularProgress size={16} /> : undefined}
                    >
                        {assigning ? 'Mapping...' : 'Map & replay'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View punches dialog */}
            <Dialog open={!!viewTarget} onClose={closeViewDialog} fullWidth maxWidth="md" data-help-section="deviceUser.punches.viewDialog">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    Held punches
                    <IconButton onClick={closeViewDialog} size="small">
                        <CloseOutlined />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {viewTarget && (
                        <Box sx={{ mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                            Device: {viewTarget.deviceSerial} | Enrolment no.: {viewTarget.deviceUserNumber}
                        </Box>
                    )}
                    {viewLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <ScrollableTable>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                                <thead>
                                    <tr>
                                        <th style={tableHeaderStyle} data-help="deviceUser.punches.col.punchTime">Punch time</th>
                                        <th style={tableHeaderStyle} data-help="deviceUser.punches.col.checkType">Check type</th>
                                        <th style={tableHeaderStyle} data-help="deviceUser.punches.col.status">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewPunches.map(p => (
                                        <tr key={p.id}>
                                            <td style={tableCellStyle}>{formatDate(p.punchTimestamp)}</td>
                                            <td style={tableCellStyle}>{p.checkType || '-'}</td>
                                            <td style={tableCellStyle}>{p.status}</td>
                                        </tr>
                                    ))}
                                    {viewPunches.length === 0 && (
                                        <tr>
                                            <td style={tableCellStyle} colSpan={3}>No punches found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </ScrollableTable>
                    )}
                    {viewPageInfo.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={viewPageInfo.totalPages}
                                page={viewPageInfo.currentPage + 1}
                                onChange={handleViewPageChange}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Discard confirmation */}
            <ConfirmDialog
                open={!!discardTarget}
                title="Discard held punches"
                message={
                    discardTarget
                        ? `Discard ${discardTarget.count} held punch(es) for device ${discardTarget.deviceSerial}, ` +
                          `enrolment no. ${discardTarget.deviceUserNumber}? This cannot be undone.`
                        : ''
                }
                confirmLabel="Discard"
                confirmColor="warning"
                submitting={discarding}
                onCancel={() => setDiscardTarget(null)}
                onConfirm={handleDiscard}
            />
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default UnmappedPunchList;
